import { useEffect, useState } from "react";
import { inferBlobKind, signedDownloadURL } from "../../services/waveBlobUpload";

export type WaveAttachmentViewerProps = {
  waveId: string;
  s3Key: string;
  fileName?: string | null;
  mimeType?: string | null;
  onClose: () => void;
};

type ViewerMode = "image" | "pdf" | "video" | "text" | "unsupported";

function resolveViewerMode(mimeType?: string | null, fileName?: string | null): ViewerMode {
  const mime = (mimeType ?? "").toLowerCase();
  const name = (fileName ?? "").toLowerCase();
  if (mime.startsWith("image/") || /\.(jpe?g|png|gif|webp|svg|heic)$/.test(name)) {
    return "image";
  }
  if (mime === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (mime.startsWith("video/") || /\.(mp4|mov|webm|m4v)$/.test(name)) return "video";
  if (
    mime.startsWith("text/") ||
    mime === "application/json" ||
    /\.(txt|md|csv|json|log|xml|html?)$/.test(name)
  ) {
    return "text";
  }
  if (mimeType || fileName) {
    const kind = inferBlobKind({
      type: mimeType ?? "",
      name: fileName ?? "file",
    } as File);
    if (kind === "image") return "image";
    if (kind === "pdf") return "pdf";
    if (kind === "video") return "video";
  }
  return "unsupported";
}

export function WaveAttachmentViewer({
  waveId,
  s3Key,
  fileName,
  mimeType,
  onClose,
}: WaveAttachmentViewerProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const mode = resolveViewerMode(mimeType, fileName);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      setTextContent(null);
      try {
        const signed = await signedDownloadURL(s3Key, waveId, true, fileName ?? undefined);
        if (cancelled) return;
        setUrl(signed);
        if (mode === "text") {
          const res = await fetch(signed);
          const text = await res.text();
          if (!cancelled) setTextContent(text.slice(0, 200_000));
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Impossibile aprire l'allegato");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [waveId, s3Key, fileName, mode]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      className="wave-attachment-viewer-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="wave-attachment-viewer"
        role="dialog"
        aria-modal="true"
        aria-label={fileName ?? "Allegato"}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="wave-attachment-viewer-header">
          <strong>{fileName ?? "Allegato"}</strong>
          <button type="button" className="icon-btn" aria-label="Chiudi" onClick={onClose}>
            ✕
          </button>
        </header>
        <div className="wave-attachment-viewer-body">
          {loading && <p className="muted">Caricamento…</p>}
          {error && <p className="invite-feedback error">{error}</p>}
          {!loading && !error && url && mode === "image" && (
            <img src={url} alt={fileName ?? "Immagine allegato"} className="wave-attachment-image" />
          )}
          {!loading && !error && url && mode === "pdf" && (
            <iframe title={fileName ?? "PDF"} src={url} className="wave-attachment-pdf" />
          )}
          {!loading && !error && url && mode === "video" && (
            <video src={url} controls className="wave-attachment-video">
              <track kind="captions" />
            </video>
          )}
          {!loading && !error && mode === "text" && (
            <pre className="wave-attachment-text">{textContent ?? ""}</pre>
          )}
          {!loading && !error && mode === "unsupported" && url && (
            <div className="wave-attachment-fallback">
              <p className="muted">Anteprima non disponibile per questo tipo di file.</p>
              <a href={url} target="_blank" rel="noopener noreferrer" className="primary-btn">
                Apri in nuova scheda
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
